import { useState, useEffect } from "react";
import api from "@/utils/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { EmailOptInToggle } from "@/components/EmailOptInToggle";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { QuoteLifecycle } from "@/components/quotes/QuoteLifecycle";

const downloadSar = async (m) => {
  try {
    const { data } = await api.get(`/members/${m.id}/data-export`);
    const href = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
    Object.assign(document.createElement("a"), { href, download: `data-export-${(m.name || m.email || m.id).replace(/[^a-z0-9]+/gi, "-")}.json` }).click();
    URL.revokeObjectURL(href);
  } catch { toast.error("Export failed"); }
};
import { QuoteStageBadge } from "@/components/quotes/QuoteStageBadge";
import { Award, Calendar, Mail, Phone, Building2, MapPin, Key, Archive, ArchiveRestore, Pencil, X, Save, FileText, Plus, Check, ChevronDown, Hash, Copy, ClipboardCheck } from "lucide-react";

const statusBadge = { active: "bg-emerald-100 text-emerald-700 border-emerald-200", expiring_soon: "bg-amber-100 text-amber-700 border-amber-200", expired: "bg-red-100 text-red-700 border-red-200" };
const statusLabel = { active: "Active", expiring_soon: "Expiring", expired: "Expired" };
const roleBadge = { admin: "bg-sky-100 text-sky-700 border-sky-200", staff: "bg-emerald-100 text-emerald-700 border-emerald-200", student: "bg-amber-100 text-amber-700 border-amber-200" };

export default function MemberProfile({ memberId, open, onClose, onUpdate, isAdmin, compact = false }) {
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [emailCopied, setEmailCopied] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editCompany, setEditCompany] = useState("");
  const [editAddr1, setEditAddr1] = useState("");
  const [editAddr2, setEditAddr2] = useState("");
  const [editCity, setEditCity] = useState("");
  const [editCounty, setEditCounty] = useState("");
  const [editPostcode, setEditPostcode] = useState("");
  const [editCountry, setEditCountry] = useState("United Kingdom");
  const [businessList, setBusinessList] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState("");
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [noteType, setNoteType] = useState("general");
  // Quotes tab state
  const [quotes, setQuotes] = useState([]);
  const [qualifications, setQualifications] = useState([]);
  const [courses, setCourses] = useState([]);
  const emptyQuoteForm = { qualification_ids: [], course_ids: [], manual_dates: [], manualDraft: "", quote_date: new Date().toISOString().slice(0, 10), amount: "", vat_applicable: false, status: "sent", notes: "", invoice_reference: "", po_reference: "" };
  const [quoteForm, setQuoteForm] = useState(emptyQuoteForm);
  const [quoteEditingId, setQuoteEditingId] = useState(null);
  const [qualPickerOpen, setQualPickerOpen] = useState(false);
  const [qualSearch, setQualSearch] = useState("");

  useEffect(() => {
    if (!memberId || !open) return;
    setLoading(true);
    setTab("overview");
    Promise.all([
      api.get(`/members/${memberId}`),
      api.get(`/activity/${memberId}`),
      api.get(`/quotes?member_id=${memberId}`),
      api.get(`/qualifications`),
      api.get(`/courses?limit=500`),
      api.get(`/businesses`),
    ]).then(([m, n, q, qs, cs, bs]) => {
      setMember(m.data);
      setNotes(n.data);
      setQuotes(q.data.quotes || []);
      setQualifications(qs.data || []);
      const todayStr = new Date().toISOString().slice(0, 10);
      const upcoming = (cs.data.courses || []).filter(c => (c.start_date || "") >= todayStr);
      setCourses(upcoming);
      setBusinessList(bs.data || []);
      setLoading(false);
    }).catch((err) => { console.error("Failed to load member profile:", err); setLoading(false); });
  }, [memberId, open]);

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      const { data } = await api.post("/activity", { member_id: memberId, content: newNote, note_type: noteType });
      setNotes([data, ...notes]);
      setNewNote("");
      toast.success("Note added");
    } catch (err) { console.error("Failed to add note:", err); toast.error("Failed to add note"); }
  };

  const deleteNote = async (id) => {
    try {
      await api.delete(`/activity/${id}`);
      setNotes(notes.filter(n => n.id !== id));
    } catch (err) {
      console.error("Failed to delete note:", err);
      toast.error("Couldn't delete note");
    }
  };

  // --- Quotes helpers ---
  const resetQuoteForm = () => setQuoteForm({ ...emptyQuoteForm, quote_date: new Date().toISOString().slice(0, 10) });

  const saveQuote = async () => {
    const payload = {
      member_id: memberId,
      qualifications: quoteForm.qualification_ids,
      course_ids: quoteForm.course_ids,
      manual_dates: quoteForm.manual_dates,
      quote_date: quoteForm.quote_date,
      amount: parseFloat(quoteForm.amount) || 0,
      vat_applicable: quoteForm.vat_applicable,
      notes: quoteForm.notes.trim(),
      ...(quoteEditingId
        ? { invoice_reference: quoteForm.invoice_reference, po_reference: quoteForm.po_reference }
        : { status: quoteForm.status }),
    };
    if (payload.qualifications.length === 0) { toast.error("Pick at least one qualification"); return; }
    try {
      if (quoteEditingId) {
        const { data } = await api.put(`/quotes/${quoteEditingId}`, payload);
        setQuotes(prev => prev.map(q => q.id === quoteEditingId ? data : q));
        toast.success("Quote updated");
      } else {
        const { data } = await api.post("/quotes", payload);
        setQuotes(prev => [data, ...prev]);
        toast.success(`Quote ${data.quote_code} logged`);
      }
      setQuoteEditingId(null);
      resetQuoteForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save quote");
    }
  };

  const editQuote = (q) => {
    setQuoteEditingId(q.id);
    setQuoteForm({
      qualification_ids: (q.qualifications || []).map(x => x.id),
      course_ids: q.course_ids || [],
      manual_dates: q.manual_dates || [],
      manualDraft: "",
      quote_date: q.quote_date || new Date().toISOString().slice(0, 10),
      amount: q.amount ?? "",
      vat_applicable: !!q.vat_applicable,
      status: q.status || "sent",
      notes: q.notes || "",
      invoice_reference: q.invoice_reference || "",
      po_reference: q.po_reference || "",
    });
  };

  const deleteQuote = async (id) => {
    if (!window.confirm("Delete this quote log entry?")) return;
    try {
      await api.delete(`/quotes/${id}`);
      setQuotes(prev => prev.filter(q => q.id !== id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't delete");
    }
  };

  const toggleQualification = (qid) => {
    setQuoteForm(prev => {
      const has = prev.qualification_ids.includes(qid);
      return { ...prev, qualification_ids: has ? prev.qualification_ids.filter(x => x !== qid) : [...prev.qualification_ids, qid] };
    });
  };

  const toggleCourse = (cid) => {
    setQuoteForm(prev => {
      const has = prev.course_ids.includes(cid);
      return { ...prev, course_ids: has ? prev.course_ids.filter(x => x !== cid) : [...prev.course_ids, cid] };
    });
  };

  const addManualDate = () => {
    const d = (quoteForm.manualDraft || "").trim();
    if (!d) return;
    setQuoteForm(prev => prev.manual_dates.includes(d)
      ? { ...prev, manualDraft: "" }
      : { ...prev, manual_dates: [...prev.manual_dates, d], manualDraft: "" });
  };

  const removeManualDate = (d) => setQuoteForm(prev => ({ ...prev, manual_dates: prev.manual_dates.filter(x => x !== d) }));

  // Filter upcoming courses to the picked qualifications so the dropdown
  // stays focused. When no qualifications are selected yet we show all
  // upcoming courses so the picker is still useful.
  const filteredCourses = (() => {
    const qids = quoteForm.qualification_ids;
    if (qids.length === 0) return courses;
    return courses.filter(c => qids.includes(c.qualification_id));
  })();

  const filteredQualifications = (() => {
    const q = qualSearch.trim().toLowerCase();
    if (!q) return qualifications;
    return qualifications.filter(x =>
      (x.name || "").toLowerCase().includes(q) ||
      (x.code || "").toLowerCase().includes(q) ||
      (x.category || "").toLowerCase().includes(q)
    );
  })();

  const selectedQualNames = qualifications
    .filter(x => quoteForm.qualification_ids.includes(x.id))
    .map(x => x.code || x.name);

  const handleResetPassword = async () => {
    try {
      await api.post(`/members/${memberId}/reset-password`, { new_password: newPassword });
      toast.success("Password reset successfully");
      setResetOpen(false); setNewPassword("");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to reset password"); }
  };

  const openEdit = () => {
    setEditName(member.name || "");
    setEditEmail(member.email || "");
    setEditPhone(member.phone || "");
    setEditCompany(member.company || "");
    setEditAddr1(member.address_line1 || "");
    setEditAddr2(member.address_line2 || "");
    setEditCity(member.city || "");
    setEditCounty(member.county || "");
    setEditPostcode(member.postcode || "");
    setEditCountry(member.country || "United Kingdom");
    setEditError("");
    setEditOpen(true);
  };

  // Click-to-copy email with a 2-second ✓ feedback. Falls back to a hidden
  // textarea for older browsers that don't expose navigator.clipboard.
  const copyEmail = async () => {
    const value = member?.email || "";
    if (!value) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement("textarea");
        ta.value = value; document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
      }
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 1800);
    } catch (err) {
      toast.error("Couldn't copy — select the email manually");
    }
  };

  const handleSaveEdit = async () => {
    const name = editName.trim();
    const email = editEmail.trim();
    if (!name) { setEditError("Name is required"); return; }
    if (member.role !== "student" && !email) {
      setEditError("Email is required for team members");
      return;
    }
    const payload = {
      name,
      email,
      phone: editPhone,
      company: editCompany,
      address_line1: editAddr1,
      address_line2: editAddr2,
      city: editCity,
      county: editCounty,
      postcode: editPostcode,
      country: editCountry,
    };
    setEditError("");
    setSavingEdit(true);
    try {
      const { data } = await api.put(`/members/${memberId}`, payload);
      setMember(prev => ({ ...prev, ...data }));
      toast.success("Details updated");
      setEditOpen(false);
      onUpdate?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setEditError(typeof detail === "string" ? detail : "Could not save changes");
    } finally { setSavingEdit(false); }
  };

  const handleArchive = async () => {
    try {
      if (member.status === "archived") {
        await api.post(`/members/${memberId}/unarchive`);
        toast.success("Member restored");
      } else {
        await api.post(`/members/${memberId}/archive`);
        toast.success("Member archived");
      }
      onUpdate?.();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Action failed"); }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={`${compact ? "max-w-[640px] max-h-[82vh]" : "max-w-2xl max-h-[90vh]"} overflow-y-auto overflow-x-hidden grid-cols-[minmax(0,1fr)] p-0 pr-2`} overlayClassName={compact ? "bg-black/30" : undefined} data-testid="member-profile-dialog">
        {loading || !member ? (
          <div className="p-8 text-center text-muted-foreground animate-pulse">Loading profile...</div>
        ) : (
          <>
            {/* Header */}
            <div className="p-6 pr-12 border-b border-border">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-12 h-12 shrink-0 rounded-full bg-slate-200 flex items-center justify-center text-lg font-bold text-slate-600">
                    {member.name?.[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <h2 className="font-heading font-bold text-xl tracking-tight break-words">{member.name}</h2>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <Badge variant="outline" className={`text-[10px] capitalize ${roleBadge[member.role] || ""}`}>{member.role}</Badge>
                      {member.status === "archived" && <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-500">Archived</Badge>}
                      {member.email_opt_in && <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-200" data-testid="member-opted-in-badge">Emails on</Badge>}
                      {isAdmin && (
                        <button type="button" onClick={() => downloadSar(member)} className="text-[10px] font-medium text-slate-500 hover:text-slate-900 underline underline-offset-2" title="Subject access request: download everything held about this person" data-testid="member-data-export-btn">Export data (SAR)</button>
                      )}
                      {quotes[0] && (
                        <button type="button" onClick={() => setTab("quotes")} className="inline-flex items-center gap-1 rounded hover:bg-slate-50" title="Latest quote — open Quotes tab" data-testid="member-latest-quote">
                          <QuoteStageBadge quote={quotes[0]} showCode />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1">
                  {isAdmin && (
                    <>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={openEdit} data-testid="edit-member-btn">
                        <Pencil size={12} className="mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setResetOpen(true)} data-testid="reset-password-btn">
                        <Key size={12} className="mr-1" /> Reset Password
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handleArchive} data-testid="archive-member-btn">
                        {member.status === "archived" ? <><ArchiveRestore size={12} className="mr-1" /> Restore</> : <><Archive size={12} className="mr-1" /> Archive</>}
                      </Button>
                    </>
                  )}
                </div>
              </div>
              {member.role === "student" && member.email && isAdmin && (
                <div className="mt-4"><EmailOptInToggle url={`/members/${memberId}/email-opt-in`} value={member.email_opt_in} name={member.name} onChanged={(v) => setMember((m) => ({ ...m, email_opt_in: v }))} /></div>
              )}
              {/* Contact info */}
              <div className={`grid grid-cols-2 ${compact ? "" : "md:grid-cols-4"} gap-3 mt-4 text-sm`}>
                <div className={`flex items-center gap-2 text-muted-foreground min-w-0 col-span-2 ${compact ? "" : "md:col-span-1"}`}>
                  <Mail size={14} className="shrink-0" />
                  <span className="truncate flex-1" title={member.email}>{member.email || "—"}</span>
                  {member.email && (
                    <button
                      type="button"
                      onClick={copyEmail}
                      className={`p-1 rounded transition-colors shrink-0 ${emailCopied ? "text-emerald-600 bg-emerald-50" : "text-slate-400 hover:text-sky-600 hover:bg-sky-50"}`}
                      title={emailCopied ? "Copied!" : "Copy email"}
                      data-testid="copy-email-btn"
                    >
                      {emailCopied ? <ClipboardCheck size={14} /> : <Copy size={14} />}
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2 text-muted-foreground"><Phone size={14} /><span>{member.phone || "—"}</span></div>
                <div className="flex items-center gap-2 text-muted-foreground min-w-0"><Building2 size={14} className="shrink-0" /><span className="truncate" title={member.company || ""}>{member.company || "—"}</span></div>
                <div className="flex items-center gap-2 text-muted-foreground"><MapPin size={14} /><span className="truncate" title={[member.address_line1, member.address_line2, member.city, member.county, member.postcode, member.country].filter(Boolean).join(", ") || member.address || ""}>{[member.address_line1, member.city, member.postcode].filter(Boolean).join(", ") || member.address || "—"}</span></div>
              </div>
              {/* Stats */}
              <div className="grid grid-cols-4 gap-3 mt-4">
                <div className="border border-border rounded-md p-2 text-center"><p className="text-lg font-bold text-sky-600">{member.stats?.active_certificates || 0}</p><p className="text-[10px] text-muted-foreground">Active Certs</p></div>
                <div className="border border-border rounded-md p-2 text-center"><p className="text-lg font-bold text-amber-600">{member.stats?.expiring_certificates || 0}</p><p className="text-[10px] text-muted-foreground">Expiring</p></div>
                <div className="border border-border rounded-md p-2 text-center"><p className="text-lg font-bold text-emerald-600">{member.stats?.courses_passed || 0}</p><p className="text-[10px] text-muted-foreground">Passed</p></div>
                <div className="border border-border rounded-md p-2 text-center"><p className="text-lg font-bold text-foreground">{member.stats?.total_enrollments || 0}</p><p className="text-[10px] text-muted-foreground">Enrolments</p></div>
              </div>
            </div>

            {/* Tabs */}
            <Tabs value={tab} onValueChange={setTab} className="px-6 pt-4 pb-6">
              <TabsList className="h-8 mb-4 max-w-full overflow-x-auto justify-start" data-testid="profile-tabs">
                <TabsTrigger value="overview" className="text-xs">Qualifications</TabsTrigger>
                <TabsTrigger value="certificates" className="text-xs">Certificates</TabsTrigger>
                <TabsTrigger value="courses" className="text-xs">Courses</TabsTrigger>
                <TabsTrigger value="quotes" className="text-xs">Quotes {quotes.length > 0 && <span className="ml-1 text-[10px] bg-sky-100 text-sky-700 rounded-full px-1.5">{quotes.length}</span>}</TabsTrigger>
                <TabsTrigger value="notes" className="text-xs">Notes</TabsTrigger>
              </TabsList>

              <TabsContent value="overview">
                <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Active Qualifications Held</h4>
                {(member.active_qualifications || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No active qualifications</p>
                ) : (
                  <div className="space-y-2">
                    {member.active_qualifications.map(q => (
                      <div key={q.id} className="flex items-center justify-between p-2.5 border border-border rounded-md">
                        <div>
                          <p className="text-sm font-medium">{q.name}</p>
                          <p className="text-xs text-muted-foreground">{q.category} | {q.accreditation_body}</p>
                        </div>
                        <Badge variant="outline" className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200">Active</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="certificates">
                {(member.certificates || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No certificates</p>
                ) : (
                  <div className="space-y-2">
                    {member.certificates.map(c => (
                      <div key={c.id} className="flex items-center justify-between p-2.5 border border-border rounded-md">
                        <div>
                          <p className="text-sm font-medium">{c.qualification_name}</p>
                          <div className="flex gap-3 text-xs text-muted-foreground mt-0.5">
                            <span>No: {c.certificate_number || "—"}</span>
                            <span>Issued: {c.issue_date}</span>
                            <span>Expires: {c.expiry_date}</span>
                          </div>
                        </div>
                        <Badge variant="outline" className={`text-[10px] ${statusBadge[c.computed_status] || ""}`}>
                          {statusLabel[c.computed_status] || c.computed_status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="courses">
                {(member.enrollments || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No course enrolments</p>
                ) : (
                  <div className="space-y-2">
                    {member.enrollments.map(e => (
                      <div key={e.id} className="flex items-center justify-between p-2.5 border border-border rounded-md">
                        <div>
                          <p className="text-sm font-medium">{e.course_title}</p>
                          <p className="text-xs text-muted-foreground">Enrolled: {e.enrolled_at ? new Date(e.enrolled_at).toLocaleDateString() : "—"}</p>
                        </div>
                        <Badge variant="outline" className={`text-[10px] capitalize ${
                          e.status === "passed" ? "bg-emerald-100 text-emerald-700 border-emerald-200" :
                          e.status === "failed" ? "bg-red-100 text-red-700 border-red-200" :
                          "bg-sky-100 text-sky-700 border-sky-200"
                        }`}>{e.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="quotes">
                {/* Add / edit quote form */}
                <div className="border border-sky-200 bg-sky-50/50 rounded-md p-3 mb-3 space-y-3" data-testid="quote-form">
                  {/* Qualification multi-select picker */}
                  <div>
                    <Label className="text-[10px] uppercase tracking-wide text-sky-800">Qualifications / topics</Label>
                    <Popover open={qualPickerOpen} onOpenChange={setQualPickerOpen}>
                      <PopoverTrigger asChild>
                        <button type="button" className="w-full flex items-center justify-between gap-2 h-9 px-2 rounded-md border border-border bg-white text-left text-xs hover:bg-slate-50" data-testid="quote-qual-trigger">
                          <span className="truncate flex-1">
                            {selectedQualNames.length === 0
                              ? <span className="text-muted-foreground">Pick one or more…</span>
                              : <span className="text-foreground">{selectedQualNames.slice(0, 4).join(", ")}{selectedQualNames.length > 4 ? ` +${selectedQualNames.length - 4}` : ""}</span>}
                          </span>
                          <ChevronDown size={12} className="text-muted-foreground" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[380px] p-0" align="start">
                        <div className="p-2 border-b border-border">
                          <Input autoFocus value={qualSearch} onChange={(e) => setQualSearch(e.target.value)} placeholder="Search by name, code, category…" className="h-8 text-xs" data-testid="quote-qual-search" />
                        </div>
                        <div className="max-h-[260px] overflow-y-auto">
                          {filteredQualifications.length === 0 ? (
                            <p className="p-3 text-xs text-muted-foreground text-center">No matches</p>
                          ) : filteredQualifications.map(q => {
                            const checked = quoteForm.qualification_ids.includes(q.id);
                            return (
                              <label key={q.id} className="flex items-start gap-2 px-3 py-1.5 hover:bg-sky-50 cursor-pointer border-b border-slate-50" data-testid={`quote-qual-opt-${q.id}`}>
                                <Checkbox checked={checked} onCheckedChange={() => toggleQualification(q.id)} className="mt-0.5" />
                                <div className="min-w-0 flex-1 text-xs">
                                  <div className="font-medium truncate text-foreground">{q.code ? `${q.code} — ${q.name}` : q.name}</div>
                                  <div className="text-[10px] text-muted-foreground">{q.category}</div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                        <div className="p-2 border-t border-border flex justify-end">
                          <Button size="sm" className="h-7 text-xs" onClick={() => setQualPickerOpen(false)} data-testid="quote-qual-done">Done</Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                    {selectedQualNames.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {selectedQualNames.map(n => (
                          <Badge key={n} variant="outline" className="text-[10px] bg-sky-100 text-sky-700 border-sky-200">{n}</Badge>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wide text-sky-800">Quote sent on</Label>
                      <Input type="date" value={quoteForm.quote_date} onChange={(e) => setQuoteForm({ ...quoteForm, quote_date: e.target.value })} className="h-8 text-xs" data-testid="quote-date" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wide text-sky-800">Amount (£)</Label>
                      <Input type="number" step="0.01" min="0" value={quoteForm.amount} onChange={(e) => setQuoteForm({ ...quoteForm, amount: e.target.value })} className="h-8 text-xs" data-testid="quote-amount" />
                      <label className="mt-1 flex items-center gap-1.5 text-[11px] text-sky-900">
                        <Checkbox checked={quoteForm.vat_applicable} onCheckedChange={(v) => setQuoteForm({ ...quoteForm, vat_applicable: !!v })} data-testid="quote-vat" /> Add VAT (20%)
                      </label>
                    </div>
                  </div>

                  {/* Dates offered: upcoming courses auto-fill + manual chips */}
                  <div>
                    <Label className="text-[10px] uppercase tracking-wide text-sky-800">Dates offered — pick upcoming courses</Label>
                    {filteredCourses.length === 0 ? (
                      <p className="text-[11px] text-muted-foreground italic mt-1">
                        {quoteForm.qualification_ids.length === 0
                          ? "Pick a qualification first — matching upcoming courses will appear here."
                          : "No upcoming courses scheduled for the selected qualifications yet. Add manual dates below."}
                      </p>
                    ) : (
                      <div className="max-h-[140px] overflow-y-auto border border-border rounded-md bg-white mt-1 divide-y divide-slate-50" data-testid="quote-course-picker">
                        {filteredCourses.map(c => {
                          const checked = quoteForm.course_ids.includes(c.id);
                          return (
                            <label key={c.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-sky-50 cursor-pointer text-xs" data-testid={`quote-course-opt-${c.id}`}>
                              <Checkbox checked={checked} onCheckedChange={() => toggleCourse(c.id)} />
                              <span className="font-medium truncate flex-1">{c.title}</span>
                              <span className="text-[10px] text-muted-foreground whitespace-nowrap">{c.start_date}{c.end_date && c.end_date !== c.start_date ? ` → ${c.end_date}` : ""}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div>
                    <Label className="text-[10px] uppercase tracking-wide text-sky-800">Or add manual dates</Label>
                    <div className="flex items-center gap-1 mt-1">
                      <Input type="date" value={quoteForm.manualDraft} onChange={(e) => setQuoteForm({ ...quoteForm, manualDraft: e.target.value })} className="h-8 text-xs flex-1" data-testid="quote-manual-draft" />
                      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={addManualDate} data-testid="quote-manual-add"><Plus size={11}/></Button>
                    </div>
                    {quoteForm.manual_dates.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1" data-testid="quote-manual-chips">
                        {quoteForm.manual_dates.map(d => (
                          <Badge key={d} variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 pl-2 pr-1 py-0.5 flex items-center gap-1">
                            {d}
                            <button type="button" onClick={() => removeManualDate(d)} className="rounded hover:bg-amber-100 p-0.5"><X size={9}/></button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>

                  {quoteEditingId ? (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] uppercase tracking-wide text-sky-800">Invoice ref</Label>
                        <Input value={quoteForm.invoice_reference} onChange={(e) => setQuoteForm({ ...quoteForm, invoice_reference: e.target.value })} placeholder="e.g. Xero INV-0042" className="h-8 text-xs" data-testid="quote-invoice-ref" />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wide text-sky-800">PO number</Label>
                        <Input value={quoteForm.po_reference} onChange={(e) => setQuoteForm({ ...quoteForm, po_reference: e.target.value })} placeholder="Customer PO" className="h-8 text-xs" data-testid="quote-po-ref" />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <Label className="text-[10px] uppercase tracking-wide text-sky-800">Starting stage</Label>
                      <select value={quoteForm.status} onChange={(e) => setQuoteForm({ ...quoteForm, status: e.target.value })} className="h-8 w-full rounded-md border border-border bg-white px-2 text-xs" data-testid="quote-status">
                        <option value="sent">Sent</option>
                        <option value="received">Received</option>
                        <option value="accepted">Accepted</option>
                        <option value="declined">Declined</option>
                        <option value="expired">Expired</option>
                      </select>
                      <p className="text-[10px] text-sky-800/70 mt-1">Move it on later (Invoiced, Paid…) using the tracker on the quote.</p>
                    </div>
                  )}
                  <div>
                    <Label className="text-[10px] uppercase tracking-wide text-sky-800">Notes</Label>
                    <Input value={quoteForm.notes} onChange={(e) => setQuoteForm({ ...quoteForm, notes: e.target.value })} placeholder="Any extra context (voicemail, specific site, etc.)" className="h-8 text-xs" data-testid="quote-notes" />
                  </div>
                  <div className="flex justify-end gap-2">
                    {quoteEditingId && (
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setQuoteEditingId(null); resetQuoteForm(); }} data-testid="quote-cancel-edit">Cancel edit</Button>
                    )}
                    <Button size="sm" className="h-7 text-xs bg-slate-900 hover:bg-slate-800 text-white gap-1" onClick={saveQuote} data-testid="quote-save">
                      {quoteEditingId ? <><Check size={11}/>Update quote</> : <><Plus size={11}/>Log quote</>}
                    </Button>
                  </div>
                </div>
                {quotes.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-6 text-center" data-testid="quotes-empty">
                    No quotes logged yet. Log the first one above so follow-ups don't slip.
                  </p>
                ) : (
                  <div className="space-y-2 max-h-[480px] overflow-y-auto" data-testid="quotes-list">
                    {quotes.map(q => {
                      const qualNames = (q.qualifications && q.qualifications.length > 0)
                        ? q.qualifications.map(x => x.code || x.name)
                        : (q.qualification ? [q.qualification] : []);
                      return (
                        <div key={q.id} className="border border-border rounded-md p-2.5 bg-white" data-testid={`quote-${q.id}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                {q.quote_code && (
                                  <span className="inline-flex items-center gap-1 font-mono text-[10px] font-semibold text-slate-700 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5" data-testid={`quote-code-${q.id}`}>
                                    <Hash size={9} />{q.quote_code}
                                  </span>
                                )}
                                <FileText size={12} className="text-sky-600" />
                                {qualNames.map(n => (
                                  <Badge key={n} variant="outline" className="text-[10px] bg-sky-50 text-sky-700 border-sky-200">{n}</Badge>
                                ))}
                                <QuoteStageBadge quote={q} testId={`quote-stage-badge-${q.id}`} />
                                {q.amount > 0 && <span className="text-xs font-semibold text-foreground">£{Number(q.total ?? q.amount).toFixed(2)}{q.vat_applicable && <span className="ml-0.5 text-[9px] font-normal text-muted-foreground">inc VAT</span>}</span>}
                              </div>
                              <div className="text-[11px] text-muted-foreground mt-1">
                                Sent {q.quote_date}
                                {q.offered_dates && q.offered_dates.length > 0 && <> · Offered: <span className="text-foreground">{q.offered_dates.join(", ")}</span></>}
                              </div>
                              {q.notes && <p className="text-[11px] text-slate-700 bg-slate-50 border border-slate-100 rounded px-2 py-1 mt-1 italic">{q.notes}</p>}
                              <QuoteLifecycle quote={q} onUpdated={(nq) => setQuotes(prev => prev.map(x => x.id === nq.id ? nq : x))} />
                              <p className="text-[10px] text-muted-foreground mt-1">{q.created_by_name} · {new Date(q.created_at).toLocaleDateString()}</p>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <button onClick={() => editQuote(q)} className="p-1 rounded hover:bg-slate-100 text-slate-500" title="Edit" data-testid={`quote-edit-${q.id}`}><Pencil size={11}/></button>
                              <button onClick={() => deleteQuote(q.id)} className="p-1 rounded hover:bg-red-50 text-red-400" title="Delete" data-testid={`quote-delete-${q.id}`}><X size={12}/></button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="notes">
                {/* Add note form */}
                <div className="flex gap-2 mb-3">                  <select value={noteType} onChange={(e) => setNoteType(e.target.value)} className="h-8 rounded-md border border-border bg-white px-2 text-xs text-foreground" data-testid="note-type-select">
                    <option value="general">General</option>
                    <option value="call">Phone Call</option>
                    <option value="email">Email</option>
                    <option value="booking">Booking</option>
                    <option value="followup">Follow Up</option>
                  </select>
                  <Input value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a note..." className="flex-1 h-8 text-xs" onKeyDown={(e) => e.key === "Enter" && addNote()} data-testid="note-input" />
                  <Button size="sm" className="h-8 text-xs bg-slate-900 hover:bg-slate-800 text-white" onClick={addNote} data-testid="add-note-btn">Add</Button>
                </div>
                {notes.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No notes yet</p>
                ) : (
                  <div className="space-y-2 max-h-[250px] overflow-y-auto">
                    {notes.map(n => {
                      const typeIcon = { call: "Phone", email: "Email", booking: "Booking", followup: "Follow up", general: "Note" };
                      const typeColor = { call: "bg-blue-100 text-blue-700", email: "bg-violet-100 text-violet-700", booking: "bg-emerald-100 text-emerald-700", followup: "bg-amber-100 text-amber-700", general: "bg-slate-100 text-slate-600" };
                      return (
                        <div key={n.id} className="p-2.5 border border-border rounded-md">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className={`text-[9px] px-1.5 ${typeColor[n.note_type] || typeColor.general}`}>{typeIcon[n.note_type] || "Note"}</Badge>
                                <span className="text-[10px] text-muted-foreground">{n.created_by} | {new Date(n.created_at).toLocaleString()}</span>
                              </div>
                              <p className="text-sm text-foreground">{n.content}</p>
                            </div>
                            <button onClick={() => deleteNote(n.id)} className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600 shrink-0 ml-2"><X size={12} /></button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
            </Tabs>

            {/* Reset Password Dialog */}
            {resetOpen && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50 rounded-lg">
                <div className="bg-white rounded-md p-6 max-w-sm w-full mx-4" data-testid="reset-password-dialog">
                  <h3 className="font-heading font-semibold mb-3">Reset Password for {member.name}</h3>
                  <div className="mb-3"><Label className="text-xs">New Password</Label><Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 6 characters" data-testid="new-password-input" /></div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => setResetOpen(false)}>Cancel</Button>
                    <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" onClick={handleResetPassword} disabled={newPassword.length < 6} data-testid="confirm-reset-password">Reset</Button>
                  </div>
                </div>
              </div>
            )}

            {/* Edit details — proper nested Dialog so it anchors centred over
                the full viewport (not clipped by the profile's own scroll). */}
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
              <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="edit-member-dialog">
                <DialogHeader><DialogTitle className="font-heading">Edit details</DialogTitle></DialogHeader>
                {editError && (
                  <div className="mb-1 p-2 rounded bg-red-50 border border-red-200 text-red-700 text-xs" data-testid="edit-member-error">{editError}</div>
                )}
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Full name</Label>
                    <Input value={editName} onChange={(e) => setEditName(e.target.value)} data-testid="edit-member-name" />
                  </div>
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} placeholder={member.role === "student" ? "Optional" : "required"} data-testid="edit-member-email" />
                    {member.role !== "student" && (
                      <p className="text-[10px] text-muted-foreground mt-1">Team members use this email to sign in.</p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Phone</Label>
                      <Input value={editPhone} onChange={(e) => setEditPhone(e.target.value)} data-testid="edit-member-phone" />
                    </div>
                    <div>
                      <Label className="text-xs">Business</Label>
                      <Input
                        value={editCompany}
                        onChange={(e) => setEditCompany(e.target.value)}
                        list="profile-business-list"
                        placeholder="Pick or type new…"
                        data-testid="edit-member-business"
                      />
                      <datalist id="profile-business-list">
                        {businessList.map(b => <option key={b.id} value={b.name} />)}
                      </datalist>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Address line 1</Label>
                    <Input value={editAddr1} onChange={(e) => setEditAddr1(e.target.value)} data-testid="edit-member-addr1" />
                  </div>
                  <div>
                    <Label className="text-xs">Address line 2</Label>
                    <Input value={editAddr2} onChange={(e) => setEditAddr2(e.target.value)} data-testid="edit-member-addr2" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Town / city</Label><Input value={editCity} onChange={(e) => setEditCity(e.target.value)} data-testid="edit-member-city" /></div>
                    <div><Label className="text-xs">County</Label><Input value={editCounty} onChange={(e) => setEditCounty(e.target.value)} data-testid="edit-member-county" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Postcode</Label><Input value={editPostcode} onChange={(e) => setEditPostcode(e.target.value)} data-testid="edit-member-postcode" /></div>
                    <div><Label className="text-xs">Country</Label><Input value={editCountry} onChange={(e) => setEditCountry(e.target.value)} data-testid="edit-member-country" /></div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" size="sm" onClick={() => setEditOpen(false)} data-testid="cancel-edit-member">Cancel</Button>
                  <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white gap-1" onClick={handleSaveEdit} disabled={savingEdit} data-testid="save-edit-member">
                    <Save size={12} /> {savingEdit ? "Saving…" : "Save"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
